import { useParams } from "react-router";
import { BackArrowIcon } from "vega-icons";

import { getUserStatusVariant } from "@vega-api/custom/utils/getUserStatusVariant";
import { UserStatus } from "@vega-api/generated/types/userStatus";
import { useGetUserById } from "@vega-api/generated/user-management-v1/user-management-v1";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { Tag } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import {
  DateFormatString,
  formatShortSlashDate,
} from "@vega-core/utils/formatDate";
import {
  convertToTitleCase,
  formatFullName,
} from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { VegaModulesPaths } from "../../../../../paths";
import { SettingsSection } from "../../../constants";

import { FundsAccess } from "./FundsAccess";

export const UserProfile = () => {
  const { id: userId = "" } = useParams();

  const { data: user, isLoading } = useGetUserById(userId, {
    query: { select: (response) => response.data },
  });

  const displayName =
    isTruthy(user?.firstName) && isTruthy(user.lastName)
      ? formatFullName({ firstName: user.firstName, lastName: user.lastName })
      : m.titles_users();

  const lastActivityLabel =
    user?.lastActivity != null
      ? m.titles_lastActivityFormatted({
          date: formatShortSlashDate(
            user.lastActivity,
            DateFormatString.US_DATE_WITH_COMMA,
          ),
        })
      : null;

  return (
    <div className="flex w-full flex-1 flex-col gap-6">
      {isLoading ? <LoadingSpinner /> : null}

      <ThemedButton
        className="px-0"
        dataTestId="back-to-users"
        route={`${VegaModulesPaths.Settings}/${SettingsSection.Users}`}
        variant="tertiary"
      >
        <div className="flex items-center gap-2">
          <BackArrowIcon />
          {m.btn_back()}
        </div>
      </ThemedButton>

      {isTruthy(user) ? (
        <>
          <div className="flex items-start gap-4 rounded-2.5 border border-border/primary bg-background/secondary p-6">
            <Avatar
              className="h-12 w-12 shrink-0"
              fontClassName="heading-3 text-button/primary/foreground/default"
              initials={
                isTruthy(user.firstName) ? user.firstName[0] : user.email[0]
              }
              variant={
                user.status === UserStatus.ACTIVE ? "primary" : "secondary"
              }
            />
            <div className="flex flex-col gap-1 min-w-0">
              <span className="body-extra-large-semibold text-text/primary">
                {displayName}
              </span>
              <span className="body-base-regular text-text/secondary truncate">
                {user.email}
              </span>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <Tag variant={getUserStatusVariant(user.status)}>
                  {convertToTitleCase(user.status)}
                </Tag>
                {lastActivityLabel != null ? (
                  <span className="body-base-regular rounded-default border border-border/primary bg-background/primary px-2.5 py-0.5 text-text/secondary">
                    {lastActivityLabel}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <FundsAccess />
        </>
      ) : null}
    </div>
  );
};
